import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, CreditCard, DollarSign, Megaphone, Landmark, Plus, Loader2, Trash2 } from 'lucide-react';
import DashboardLayout, { NavCard } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FinancialMaterials() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { language } = useLanguage();
  
  const [activeSection, setActiveSection] = useState('main');
  const [loading, setLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMaterial, setNewMaterial] = useState({ name_ar: '', name_en: '', price: '', description: '' });
  
  const getHeaders = () => ({ Authorization: `Bearer ${token}` });
  
  // Fetch materials
  useEffect(() => {
    const fetchMaterials = async () => {
      if (!token || activeSection === 'main') return;
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/financial-materials?category=${activeSection}`, { headers: getHeaders() });
        setMaterials(res.data.data || []);
      } catch (err) {
        console.log('Error fetching materials:', err);
        setMaterials([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMaterials();
  }, [activeSection, token]);
  
  const handleAddMaterial = async () => {
    if (!newMaterial.name_ar.trim()) {
      toast.error(language === 'ar' ? 'الاسم مطلوب' : 'Name is required');
      return;
    }
    
    try {
      const res = await axios.post(`${API_URL}/api/admin/financial-materials`, {
        ...newMaterial,
        category: activeSection
      }, { headers: getHeaders() });
      
      setMaterials([...materials, res.data.data]);
      setShowAddModal(false);
      setNewMaterial({ name_ar: '', name_en: '', price: '', description: '' });
      toast.success(language === 'ar' ? 'تم إضافة المادة بنجاح' : 'Material added successfully');
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في إضافة المادة' : 'Failed to add material');
    }
  };
  
  const handleDeleteMaterial = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل تريد حذف هذه المادة؟' : 'Delete this material?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/financial-materials/${id}`, { headers: getHeaders() });
      setMaterials(materials.filter(m => m.id !== id));
      toast.success(language === 'ar' ? 'تم الحذف بنجاح' : 'Deleted successfully');
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في الحذف' : 'Failed to delete');
    }
  };
  
  const handleBack = () => {
    if (activeSection !== 'main') {
      setActiveSection('main');
    } else {
      navigate('/dashboard/finance/cloudgm/customers');
    }
  };
  
  const categories = [
    { id: 'subscriptions', nameAr: 'مواد الاشتراكات', nameEn: 'Subscription Materials', icon: CreditCard, color: '#3b82f6' },
    { id: 'payments', nameAr: 'مواد الدفعات', nameEn: 'Payment Materials', icon: DollarSign, color: '#10b981' },
    { id: 'ads', nameAr: 'مواد مدفوعات الإعلانات', nameEn: 'Ad Payment Materials', icon: Megaphone, color: '#f97316' },
    { id: 'government', nameAr: 'مواد الخدمات الحكومية', nameEn: 'Government Services Materials', icon: Landmark, color: '#f59e0b' },
  ];
  
  const currentCategory = categories.find(c => c.id === activeSection);

  // Get breadcrumb based on active section
  const getBreadcrumb = () => {
    const base = [
      { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
      { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard/finance/cloudgm' },
      { labelAr: 'منصة العملاء (المالية)', labelEn: 'Customer Platform (Finance)', path: '/dashboard/finance/cloudgm/customers' },
      { labelAr: 'الموارد المالية', labelEn: 'Financial Resources', path: activeSection === 'main' ? null : '/dashboard/finance/cloudgm/customers/materials' }
    ];
    
    if (activeSection !== 'main' && currentCategory) {
      base.push({
        labelAr: currentCategory.nameAr,
        labelEn: currentCategory.nameEn,
        path: null
      });
    }
    
    return base;
  };

  return (
    <DashboardLayout
      title={activeSection === 'main' 
        ? (language === 'ar' ? 'الموارد المالية' : 'Financial Resources')
        : (language === 'ar' ? currentCategory?.nameAr : currentCategory?.nameEn)}
      titleEn={activeSection === 'main' ? 'Financial Resources' : currentCategory?.nameEn}
      subtitle={language === 'ar' ? 'إدارة المواد المالية للمنصات' : 'Manage financial materials'}
      subtitleEn="Manage financial materials"
      screenCode="FIN-MAT"
      backPath={activeSection === 'main' ? '/dashboard/finance/cloudgm/customers' : null}
      onBack={activeSection !== 'main' ? () => setActiveSection('main') : undefined}
      breadcrumb={getBreadcrumb()}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeSection === 'main' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <Button
                  key={cat.id}
                  onClick={() => setActiveSection(cat.id)}
                  className="h-32 bg-[#1a1a2e] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] flex flex-col items-center justify-center gap-3 transition-all rounded-2xl"
                  style={{ '--hover-border': cat.color }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = cat.color}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#2a2a4e'}
                  data-testid={`btn-${cat.id}`}
                >
                  <Icon className="h-10 w-10" style={{ color: cat.color }} />
                  <span className="font-bold text-lg">{language === 'ar' ? cat.nameAr : cat.nameEn}</span>
                </Button>
              );
            })}
          </div>
        )}
        
        {/* Category Content */}
        {activeSection !== 'main' && currentCategory && (
          <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <currentCategory.icon className="h-8 w-8" style={{ color: currentCategory.color }} />
                <h2 className="text-xl font-bold text-white">
                  {language === 'ar' ? currentCategory.nameAr : currentCategory.nameEn}
                </h2>
              </div>
              <Button
                onClick={() => setShowAddModal(true)}
                className="text-white"
                style={{ backgroundColor: currentCategory.color }}
              >
                <Plus className="h-4 w-4 me-2" />
                {language === 'ar' ? 'إضافة مادة' : 'Add Material'}
              </Button>
            </div>
            
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: currentCategory.color }} />
              </div>
            ) : materials.length === 0 ? (
              <div className="text-center py-16 bg-[#0a0a0a] rounded-2xl border border-[#2a2a4e]">
                <Package className="h-16 w-16 mx-auto mb-4" style={{ color: `${currentCategory.color}30` }} />
                <p className="text-[#808080] text-lg mb-2">{language === 'ar' ? 'لا توجد مواد مالية' : 'No financial materials'}</p>
                <p className="text-[#606060] text-sm">{language === 'ar' ? 'أضف مواد مالية جديدة' : 'Add new financial materials'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {materials.map((material) => (
                  <div
                    key={material.id}
                    className="flex items-center justify-between p-4 bg-[#0a0a0a] rounded-xl border border-[#2a2a4e] hover:border-[#3a3a5e] transition-colors"
                  >
                    <div>
                      <h3 className="text-white font-medium">{language === 'ar' ? material.name_ar : material.name_en}</h3>
                      {material.price && (
                        <p className="text-sm" style={{ color: currentCategory.color }}>{material.price} {language === 'ar' ? 'درهم' : 'AED'}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteMaterial(material.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
      
      {/* Add Material Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {language === 'ar' ? 'إضافة مادة جديدة' : 'Add New Material'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm text-[#808080] mb-2">{language === 'ar' ? 'الاسم بالعربي' : 'Name (Arabic)'}</label>
              <Input
                value={newMaterial.name_ar}
                onChange={(e) => setNewMaterial({...newMaterial, name_ar: e.target.value})}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-[#808080] mb-2">{language === 'ar' ? 'الاسم بالإنجليزي' : 'Name (English)'}</label>
              <Input
                value={newMaterial.name_en}
                onChange={(e) => setNewMaterial({...newMaterial, name_en: e.target.value})}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-[#808080] mb-2">{language === 'ar' ? 'السعر (درهم)' : 'Price'}</label>
              <Input
                type="number"
                value={newMaterial.price}
                onChange={(e) => setNewMaterial({...newMaterial, price: e.target.value})}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-[#808080] mb-2">{language === 'ar' ? 'الوصف' : 'Description'}</label>
              <Input
                value={newMaterial.description}
                onChange={(e) => setNewMaterial({...newMaterial, description: e.target.value})}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-[#2a2a4e] text-white">
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleAddMaterial} className="bg-purple-600 hover:bg-purple-700 text-white">
              {language === 'ar' ? 'إضافة' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
